import React, { useEffect, useMemo, useState } from 'react';
import './SurveyResponsesViewer.css';
import {
  SurveyTrack,
  TRACK_LABELS,
  getAllQuestionsAcrossTracks,
  getSectionsForTrack,
} from './survey/surveyQuestions.ts';
import {
  listSurveyResponses,
  SurveyResponseRecord,
} from '../services/surveyApiService.ts';

const PAGE_SIZE = 25;

const csvEscape = (val: unknown): string => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const buildCsv = (responses: SurveyResponseRecord[]): string => {
  const questions = getAllQuestionsAcrossTracks();
  const metaCols = ['submitted_at', 'track', 'location', 'department'];
  const answerCols = questions.flatMap(q => [q.id, `${q.id}_comment`]);

  const descriptionRow = [
    '# question_descriptions',
    '',
    '',
    '',
    ...questions.flatMap(q => [q.text, `Comment: ${q.text}`]),
  ];
  const headerRow = [...metaCols, ...answerCols];

  const rows = responses.map(r => {
    const base = [
      r.submittedAt,
      r.track,
      r.location,
      r.department || '',
    ];
    const answerCells = questions.flatMap(q => {
      const a = r.answers?.[q.id];
      const c = r.comments?.[q.id];
      return [a === undefined || a === null ? '' : a, c || ''];
    });
    return [...base, ...answerCells];
  });

  return [descriptionRow, headerRow, ...rows]
    .map(row => row.map(csvEscape).join(','))
    .join('\n');
};

const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const formatDate = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const computeAverage = (r: SurveyResponseRecord): string => {
  const nums: number[] = [];
  Object.values(r.answers || {}).forEach(v => {
    if (typeof v === 'number' && Number.isFinite(v)) nums.push(v);
  });
  if (nums.length === 0) return '—';
  const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
  return avg.toFixed(2);
};

const SurveyResponsesViewer: React.FC = () => {
  const [responses, setResponses] = useState<SurveyResponseRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [trackFilter, setTrackFilter] = useState<SurveyTrack | ''>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listSurveyResponses({
      track: trackFilter || undefined,
      location: locationFilter || undefined,
    })
      .then(data => {
        if (!cancelled) {
          setResponses(data);
          setPage(0);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load survey responses');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [trackFilter, locationFilter]);

  const locations = useMemo(() => {
    const set = new Set<string>();
    responses.forEach(r => { if (r.location) set.add(r.location); });
    return Array.from(set).sort();
  }, [responses]);

  const pageCount = Math.max(1, Math.ceil(responses.length / PAGE_SIZE));
  const pageItems = responses.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const handleExport = () => {
    const csv = buildCsv(responses);
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `sweetspot-survey-responses-${date}.csv`);
  };

  const renderDetail = (r: SurveyResponseRecord) => {
    const sections = getSectionsForTrack(r.track);
    return (
      <tr className="detail-row">
        <td colSpan={6}>
          <div className="detail-grid">
            {sections.map(s => (
              <div className="detail-section" key={s.id}>
                <h4>{s.icon} {s.title}</h4>
                {s.questions.map(q => {
                  const ans = r.answers?.[q.id];
                  const com = r.comments?.[q.id];
                  if (ans === undefined && !com) return null;
                  return (
                    <div className="detail-qa" key={q.id}>
                      <div className="qtext">{q.text}</div>
                      <div className="qanswer">
                        {ans !== undefined && ans !== '' ? String(ans) : <em style={{ color: '#6e918a' }}>—</em>}
                      </div>
                      {com && <div className="qcomment">💬 {com}</div>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="survey-responses-viewer" data-testid="survey-responses-viewer">
      <div className="header">
        <h2>Employee Survey Responses</h2>
        <div className="meta">{loading ? 'Loading…' : `${responses.length} response${responses.length === 1 ? '' : 's'}`}</div>
      </div>

      <div className="filters">
        <label htmlFor="filter-track">Track</label>
        <select
          id="filter-track"
          value={trackFilter}
          onChange={e => setTrackFilter((e.target.value || '') as SurveyTrack | '')}
        >
          <option value="">All tracks</option>
          <option value="retail">{TRACK_LABELS.retail}</option>
          <option value="cultivation">{TRACK_LABELS.cultivation}</option>
          <option value="hq">{TRACK_LABELS.hq}</option>
        </select>

        <label htmlFor="filter-location">Location</label>
        <select
          id="filter-location"
          value={locationFilter}
          onChange={e => setLocationFilter(e.target.value)}
        >
          <option value="">All locations</option>
          {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
        </select>

        <div className="spacer" />
        <button type="button" onClick={handleExport} disabled={responses.length === 0}>
          Export CSV
        </button>
      </div>

      {error && <div className="error">⚠️ {error}</div>}

      {!error && (
        <div className="table-wrap">
          {loading ? (
            <div className="loading">Loading responses…</div>
          ) : responses.length === 0 ? (
            <div className="empty">No responses match these filters yet.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Track</th>
                  <th>Location</th>
                  <th>Department</th>
                  <th>Avg score</th>
                  <th>Optional Comments</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map(r => {
                  const commentCount = Object.values(r.comments || {}).filter(
                    (c): c is string => typeof c === 'string' && c.trim().length > 0
                  ).length;
                  const isOpen = expanded === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        className="row"
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                      >
                        <td>{formatDate(r.submittedAt)}</td>
                        <td><span className={`badge ${r.track}`}>{TRACK_LABELS[r.track]}</span></td>
                        <td>{r.location}</td>
                        <td>{r.department || '—'}</td>
                        <td>{computeAverage(r)}</td>
                        <td>{commentCount}</td>
                      </tr>
                      {isOpen && renderDetail(r)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && pageCount > 1 && (
        <div className="pager">
          <button type="button" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Prev</button>
          {Array.from({ length: pageCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={i === page ? 'active' : ''}
              onClick={() => setPage(i)}
            >
              {i + 1}
            </button>
          ))}
          <button type="button" disabled={page >= pageCount - 1} onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}>Next →</button>
        </div>
      )}
    </div>
  );
};

export default SurveyResponsesViewer;
